import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, serverTimestamp, getDocs, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

/**
 * PlayerEntriesModal Component: Displays detailed entries and stats for a selected player.
 * @param {Object} props - Component props.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {function} props.onClose - Function to close the modal.
 * @param {Object} props.player - The player object to display.
 * @param {Array<Object>} props.mysteryOutEntries - All mystery out entries to calculate player's hits.
 * @param {Array<Object>} props.weekInfoEntries - All week info entries to calculate tournament winnings.
 * @param {(number|string)} props.overallHighestOut - The highest 'High Out' value across all players.
 * @param {function} props.onEditEntryClick - Callback function when 'Edit' is clicked for an entry.
 */
const PlayerEntriesModal = ({ isOpen, onClose, player, mysteryOutEntries, weekInfoEntries, overallHighestOut, onEditEntryClick }) => {
    // Memoize the player data for consistent and safe access
    const safePlayer = useMemo(() => {
        if (!player || typeof player !== 'object') { // ...
            return { id: crypto.randomUUID(), name: 'Unknown', entries: [], totalPoints: 0, highestOut: 0, totalWeeks: 0, isFinalist: false, rank: '', hasRaffle6Plus: false, hasRaffle8Plus: false, hasRaffle10Plus: false };
        }
        // Ensure highestOut is consistently a number for calculations in the modal
        const processedHighestOut = (player.highestOut === 'x' || isNaN(player.highestOut) || player.highestOut === undefined || player.highestOut === null) // ...
            ? 0
            : player.highestOut;

        return { // ...
            ...player,
            highestOut: processedHighestOut,
            entries: Array.isArray(player.entries) ? player.entries.filter(Boolean) : [] // Ensure entries is a clean array
        };
    }, [player]);

    // Sort player's individual entries by week, then timestamp // ...
    const sortedEntries = useMemo(() => {
        return safePlayer.entries.slice().sort((a, b) => {
            if (!a || !b) return 0;
            const weekA = a.week ?? (a.timestamp || 0); // Prioritize week, then timestamp
            const weekB = b.week ?? (b.timestamp || 0);
            return weekA - weekB;
        });
    }, [safePlayer.entries]);

    // Calculate player's Mystery Out hits and total payout
    const { mysteryOutHits, mysteryOutPayout } = useMemo(() => {
        let hits = 0;
        let payout = 0;
        const playerMysteryOutWins = (Array.isArray(mysteryOutEntries) ? mysteryOutEntries.filter(Boolean) : []).filter(
            entry => entry && typeof entry.player === 'string' && entry.player.toLowerCase() === safePlayer.name?.toLowerCase() && entry.player.toLowerCase() !== 'x'
        );
        hits = playerMysteryOutWins.length;
        payout = playerMysteryOutWins.reduce((sum, entry) => sum + (entry?.total ?? 0), 0);
        return { mysteryOutHits: hits, mysteryOutPayout: payout };
    }, [mysteryOutEntries, safePlayer.name]);

    // Calculate player's Tournament Wins (entries with 7 points)
    const tournamentWinsCount = useMemo(() => {
        return safePlayer.entries.filter(entry => entry && entry.points === 7).length;
    }, [safePlayer.entries]);

    // Calculate player's Runner Ups (entries with 6 points)
    const runnerUpsCount = useMemo(() => {
        return safePlayer.entries.filter(entry => entry && entry.points === 6).length;
    }, [safePlayer.entries]);

    // Calculate player's Tournament Winnings based on points and week info payouts
    const tournamentWinningsTotal = useMemo(() => {
        let total = 0;
        const safeWeekInfoEntries = Array.isArray(weekInfoEntries) ? weekInfoEntries.filter(Boolean) : [];

        safePlayer.entries.forEach(entry => {
            if (entry && entry.week) {
                const weekInfo = safeWeekInfoEntries.find(info => info && info.week === entry.week);
                if (weekInfo) {
                    if (entry.points === 7) {
                        total += (weekInfo.firstPlacePayout ?? 0);
                    } else if (entry.points === 6) {
                        total += (weekInfo.secondPlacePayout ?? 0);
                    } else if (entry.points === 5) {
                        total += (weekInfo.thirdPlacePayout ?? 0);
                    }
                }
            }
        });
        return total;
    }, [safePlayer.entries, weekInfoEntries]);

    // Calculate Total Cash (Mystery Out Payouts + Tournament Winnings)
    const totalCash = useMemo(() => {
        return mysteryOutPayout + tournamentWinningsTotal;
    }, [mysteryOutPayout, tournamentWinningsTotal]);


    // Determine if the current player has the overall highest out
    const isHighestOutPlayer = useMemo(() => {
        // Both safePlayer.highestOut and overallHighestOut are now guaranteed to be numbers
        return safePlayer.highestOut === overallHighestOut && safePlayer.highestOut > 0;
    }, [safePlayer.highestOut, overallHighestOut]);

    // Determine CSS class for stats grid based on presence of any conditional tiles
    const statsGridClass = useMemo(() => {
        // Only show Total Cash if both Mystery Out Payout and Tournament Winnings are > 0
        const showTotalCash = (mysteryOutPayout > 0 || tournamentWinningsTotal > 0);
        const hasConditionalTiles = (mysteryOutHits > 0 || tournamentWinsCount > 0 || runnerUpsCount > 0 || tournamentWinningsTotal > 0 || showTotalCash);
        return hasConditionalTiles ? 'has-conditional-tiles' : 'no-conditional-tiles';
    }, [mysteryOutHits, tournamentWinsCount, runnerUpsCount, tournamentWinningsTotal, mysteryOutPayout]);

    if (!isOpen || !player) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50 bg-black/60 backdrop-blur-sm">
            <div className="card rounded-xl shadow-2xl p-6 w-full max-w-lg relative border border-primary-two">
                <h2 className="text-2xl font-bold text-strong mb-2 text-center">Stats for {safePlayer.name ?? ''}</h2>
                <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-1 mb-4"> {/* Container for tags */}
                    {safePlayer.isFinalist && (
                        <span className="inline-block px-1.5 py-0.25 bg-success-soft text-success text-xs font-semibold rounded-md shadow-sm">
                            Finalist
                        </span>
                    )}
                    {safePlayer.hasRaffle6Plus && (
                        <span className="inline-block px-1.5 py-0.25 bg-info-soft text-info text-xs font-semibold rounded-md shadow-sm">
                            6+ Raffle
                        </span>
                    )}
                    {safePlayer.hasRaffle8Plus && (
                        <span className="inline-block px-1.5 py-0.25 bg-warning-soft text-warning text-xs font-semibold rounded-md shadow-sm">
                            8+ Raffle
                        </span>
                    )}
                    {safePlayer.hasRaffle10Plus && (
                        <span className="inline-block px-1.5 py-0.25 bg-warning-soft text-warning text-xs font-semibold rounded-md shadow-sm">
                            10+ Raffle
                        </span>
                    )}
                    {isHighestOutPlayer && ( // Conditionally render Highest Out tag
                        <span className="inline-block px-1.5 py-0.25 bg-primary-soft subheading-primary-1 text-xs font-semibold rounded-md shadow-sm">
                            High Out
                        </span>
                    )}
                </div>
                <div className={`grid grid-cols-2 gap-4 mb-4 text-center ${statsGridClass}`} id="modalStatsGrid">
                    <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0s' }}>
                        Total Points <span className="font-semibold text-base text-strong block">{safePlayer.totalPoints ?? 0}</span>
                    </div>
                    <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.05s' }}>
                        Total Weeks Played <span className="font-semibold text-base text-strong block">{safePlayer.totalWeeks ?? 0}</span>
                    </div>
                    <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.1s' }}>
                        Highest Out <span className="font-semibold text-base text-strong block">{safePlayer.highestOut === 0 ? 'x' : (safePlayer.highestOut ?? 'x')}</span>
                    </div>
                    <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.15s' }}>
                        Current Rank <span className="font-semibold text-base text-strong block">{safePlayer.rank ?? ''}</span>
                    </div>
                    {mysteryOutHits > 0 && (
                        <>
                            <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.2s' }}>
                                Mystery Out's Hit <span className="font-semibold text-base text-strong block">{mysteryOutHits ?? 0}</span>
                            </div>
                            <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.25s' }}>
                                Mystery Out Payout <span className="font-semibold text-base text-strong block">${mysteryOutPayout ?? 0}</span>
                            </div>
                        </>
                    )}
                    {tournamentWinsCount > 0 && (
                        <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.3s' }}>
                            Tournament Wins <span className="font-semibold text-base text-strong block">{tournamentWinsCount ?? 0}</span>
                        </div>
                    )}
                    {runnerUpsCount > 0 && (
                        <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.35s' }}>
                            Runner Ups <span className="font-semibold text-base text-strong block">{runnerUpsCount ?? 0}</span>
                        </div>
                    )}
                    {tournamentWinningsTotal > 0 && (
                        <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.4s' }}>
                            Tournament Winnings <span className="font-semibold text-base text-strong block">${tournamentWinningsTotal.toFixed(2)}</span>
                        </div>
                    )}
                    {(mysteryOutPayout > 0 || tournamentWinningsTotal > 0) && (
                        <div className="p-2 rounded-md surface-low border border-low text-sm text-muted" style={{ animationDelay: '0.45s' }}>
                            Total Cash <span className="font-semibold text-base text-strong block">${totalCash.toFixed(2)}</span>
                        </div>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-muted text-2xl font-bold hover:opacity-80"
                    aria-label="Close modal"
                >
                    &times;
                </button>
                {(Array.isArray(sortedEntries) ? sortedEntries.length : 0) > 0 ? (
                    <div className="overflow-y-auto max-h-80 border border-low rounded-lg surface-low">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">Week</th>
                                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">Points</th>
                                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">High Out</th>
                                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody className="row-alt">
                                {sortedEntries.map((entry, idx) => (
                                    <tr key={entry.id ?? entry.timestamp ?? idx} className="border-b border-low hover:opacity-95">
                                        <td className="px-4 py-2 text-center text-sm">{(entry.week !== null ? entry.week : 'N/A') ?? ''}</td>
                                        <td className="px-4 py-2 text-center text-sm">
                                            {entry.isDNP ? '2(DNP)' : (entry.points ?? 0)}
                                        </td>
                                        <td className="px-4 py-2 text-center text-sm">{(entry.highOut === 'x' || entry.highOut === 0) ? 'x' : (entry.highOut ?? 'x')}</td>
                                        <td className="px-4 py-2 text-center text-sm">
                                            <button
                                                onClick={() => onEditEntryClick(safePlayer.id, entry.week)}
                                                className="btn btn-info py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-center text-muted italic">No individual entries found for this player.</p>
                )}
            </div>
        </div>
    );
};

/**
 * OverwriteConfirmationModal Component: Prompts user to overwrite or discard a duplicate entry.
 * @param {Object} props - Component props.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {function} props.onClose - Function to close the modal (discards entry).
 *
 * @param {function} props.onConfirm - Function to call when overwrite is confirmed.
 * @param {function} props.onDiscard
 * @param {Object} props.entryData - Data for the entry to be overwritten/discarded.
 */
const OverwriteConfirmationModal = ({ isOpen, onClose, onConfirm, onDiscard, entryData }) => {
    if (!isOpen || !entryData) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="card rounded-xl shadow-2xl p-6 w-full max-w-sm relative text-center border border-primary-two">
                <h3 className="text-xl font-semibold text-strong mb-4">Duplicate Week Entry</h3>
                <p className="text-muted mb-6">
                    An entry for **Week {entryData.week ?? ''}** already exists for **{entryData.playerName ?? ''}**.
                    The current entry has Points: {entryData.oldPoints ?? 0}, High Out: {entryData.oldHighOut === 'x' ? 'x' : (entryData.oldHighOut ?? 'x')}.
                    <br/><br/>
                    Do you want to **overwrite** it with the new entry (Points: {entryData.points ?? 0}, High Out: {entryData.highOut === 'x' ? 'x' : (entryData.highOut ?? 'x')}) or **discard** the new entry?
                </p>
                <div className="flex justify-around space-x-4">
                    <button
                        onClick={onConfirm}
                        className="btn btn-warning flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300"
                    >
                        Overwrite
                    </button>
                    <button
                        onClick={onDiscard}
                        className="btn btn-danger flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300"
                    >
                        Discard
                    </button>
                </div>
            </div>
        </div>
    );
};

// Admin-only tools for managing Point Series
export const PointSeriesAdminPage = () => {
    // State to store the list of players, initialized as an empty array
    const [players, setPlayers] = useState([]);
    // States for player input form
    const [playerName, setPlayerName] = useState('');
    const [points, setPoints] = useState('');
    const [highOut, setHighOut] = useState('');
    const [weekNumber, setWeekNumber] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [partnerName, setPartnerName] = useState('');
    const [partnerHighOut, setPartnerHighOut] = useState('');
    const [dnpChecked, setDnpChecked] = useState(false);

    // State for player entry modal visibility and data
    const [isEntriesModalOpen, setIsEntriesModalOpen] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null); // Stores the player object for the modal

    // State for overwrite confirmation modal
    const [isOverwriteModalOpen, setIsOverwriteModalModalOpen] = useState(false);
    const [overwritePendingEntry, setOverwritePendingEntry] = useState(null);

    const [editingPlayerInfo, setEditingPlayerInfo] = useState(null);

    const [mysteryOutEntries, setMysteryOutEntries] = useState([]);
    const [mysteryOutWeek, setMysteryOutWeek] = useState('');
    const [mysteryOutValue, setMysteryOutValue] = useState('');
    const [mysteryOutWeeklyContribution, setMysteryOutWeeklyContribution] = useState('');
    const [mysteryOutPlayer, setMysteryOutPlayer] = useState('');
    const [editingMysteryOutId, setEditingMysteryOutId] = useState(null); // New state for editing Mystery Out

    const [weekInfoEntries, setWeekInfoEntries] = useState([]);
    const [editingWeekInfoId, setEditingWeekInfoId] = useState(null); // New state for editing Week Info

    const [seriesName, setSeriesName] = useState('Summer Point Series');
    const [isActiveSeries, setIsActiveSeries] = useState(true);
    const [isArchivedSeries, setIsArchivedSeries] = useState(false);


    const [weekInfoInputWeek, setWeekInfoInputWeek] = useState(''); // Week selected in the Week Info input form
    const [newDEChecked, setNewDEChecked] = useState(false); // Double Elimination checkbox
    const [newRRChecked, setNewRRChecked] = useState(false); // Round Robin checkbox
    const [newSinglesChecked, setNewSinglesChecked] = useState(false); // Singles checkbox
    const [newDoublesChecked, setNewDoublesChecked] = useState(false); // Doubles checkbox
    const [newFirstPlacePayout, setNewFirstPlacePayout] = useState('');
    const [newSecondPlacePayout, setNewSecondPlacePayout] = useState('');
    const [newThirdPlacePayout, setNewThirdPlacePayout] = useState('');

    const [allSeasons, setAllSeasons] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState('');

    // Modal states
    const [isCreateSeriesModalOpen, setIsCreateSeriesModalOpen] = useState(false);
    const [newSeriesName, setNewSeriesName] = useState('');
    const [newSeriesWeeks, setNewSeriesWeeks] = useState(12);

    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
    const [editedSeriesName, setEditedSeriesName] = useState('');
    const [editedSeriesYear, setEditedSeriesYear] = useState('');

    const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
    const [tempIsActive, setTempIsActive] = useState(true);
    const [tempIsArchived, setTempIsArchived] = useState(false);

    // State for the migration tool
    const [migrationStartDate, setMigrationStartDate] = useState('');
    const [migrationDayOfWeek, setMigrationDayOfWeek] = useState('2'); // Default to Tuesday
    const [newSeriesStartDate, setNewSeriesStartDate] = useState('');
    const [newSeriesDayOfWeek, setNewSeriesDayOfWeek] = useState('2'); // Default to Tuesday

    // Fetch all seasons on mount
    useEffect(() => {
        const fetchSeasons = async () => {
            try {
                const snap = await getDocs(collection(db, 'pointSeries'));
                const seasons = snap.docs.map(d => {
                    const data = d.data();
                    return { id: d.id, name: data.name || 'Unnamed Series', year: data.year || 'N/A' };
                }).sort((a, b) => (b.year || '').localeCompare(a.year || '') || (a.name || '').localeCompare(b.name || ''));
                setAllSeasons(seasons);
            } catch (e) {
                logError("Error fetching seasons:", e);
                setErrorMessage('Could not load season list.');
            }
        };
        fetchSeasons();
    }, []); // Run once

    // Set default season once seasons are loaded
    useEffect(() => {
        if (allSeasons.length > 0 && !selectedSeason) { // ...
            setSelectedSeason(allSeasons[0].id);
        }
    }, [allSeasons, selectedSeason]);

    const clearFormForNewYear = useCallback(() => {
        setSeriesName('Summer Point Series');
        setIsActiveSeries(true);
        setIsArchivedSeries(false);
        setPlayers([]);
        setWeekInfoEntries([]);
        setMysteryOutEntries([]);
    }, []);

    // Load data when a season is selected
    useEffect(() => {
        if (!selectedSeason) {
            return; // Do nothing if no season is selected
        }

        const loadSeasonData = async () => {
            try {
                const seasonDoc = await getDoc(doc(db, 'pointSeries', selectedSeason));
                if (seasonDoc.exists()) {
                    const data = seasonDoc.data();
                    setSeriesName(data.name || 'Summer Point Series');
                    setIsActiveSeries(data.active !== undefined ? data.active : true);
                    setIsArchivedSeries(data.archived !== undefined ? data.archived : false);
                    setPlayers(data.players || []);
                    const weekInfoWithIds = (data.weekInfo || []).map(wi => ({ ...wi, id: wi.id || crypto.randomUUID() }));
                    setWeekInfoEntries(weekInfoWithIds);
                    setMysteryOutEntries(data.mysteryOut || []);
                    setErrorMessage(`Loaded data for ${data.name} - ${data.year}.`);
                } else {
                    setErrorMessage(`No data found for season ${selectedSeason}. Ready to create new.`);
                    clearFormForNewYear();
                }
            } catch (e) {
                logError(`Error loading season ${selectedSeason}:`, e);
                setErrorMessage(`Error loading data for ${selectedSeason}.`);
            }
        };

        loadSeasonData();
    }, [selectedSeason, clearFormForNewYear]);

    const handleCreateSeries = async () => {
        if (!newSeriesName.trim() || !newSeriesStartDate) {
            setErrorMessage("Series name and start date are required.");
            return;
        }
        const weeks = parseInt(newSeriesWeeks, 10);
        if (isNaN(weeks) || weeks < 1) {
            setErrorMessage("Number of weeks must be a positive number.");
            return;
        }

        try { // Safer date parsing
            const getNextDayOfWeek = (date, dayOfWeek) => { // Safer date parsing
                const resultDate = new Date(date); // Safer date parsing
                const currentDay = resultDate.getUTCDay();
                const distance = (dayOfWeek - currentDay + 7) % 7;
                resultDate.setUTCDate(resultDate.getUTCDate() + distance);
                return resultDate;
            };

            const weekOneDate = getNextDayOfWeek(new Date(newSeriesStartDate.replace(/-/g, '/')), parseInt(newSeriesDayOfWeek, 10));

            const generatedWeekInfo = Array.from({ length: weeks }, (_, i) => {
                const weekDate = new Date(weekOneDate.getTime());
                weekDate.setDate(weekDate.getDate() + (i * 7));
                return {
                  id: crypto.randomUUID(),
                  week: i + 1,
                    date: weekDate.toLocaleDateString('en-US'),
                    roundRobin: false,
                    doubleElim: false,
                    singles: false,
                    doubles: false,
                  firstPlacePayout: 0, secondPlacePayout: 0, thirdPlacePayout: 0,
                  timestamp: Date.now()
                };
            });

          const newSeriesData = {
              name: newSeriesName, // Safer date parsing
              year: new Date(newSeriesStartDate.replace(/-/g, '/')).getFullYear().toString(),
              active: true,
              archived: false,
              createdAt: serverTimestamp(),
              players: [],
              weekInfo: generatedWeekInfo,
              mysteryOut: [],
              summary: {}
          };
          const docRef = await addDoc(collection(db, 'pointSeries'), newSeriesData);
          setErrorMessage(`Successfully created new series: ${newSeriesName}`);
          setIsCreateSeriesModalOpen(false);
          setNewSeriesName('');
          setNewSeriesWeeks(12);
            setNewSeriesStartDate('');
            setNewSeriesDayOfWeek('2');
          // Refetch seasons and select the new one
          const snap = await getDocs(collection(db, 'pointSeries'));
          const seasons = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id })).sort((a, b) => (b.name || b.id).localeCompare(a.name || a.id));
          setAllSeasons(seasons);
          setSelectedSeason(docRef.id);
      } catch (e) {
          logError("Error creating new series:", e);
          setErrorMessage("Failed to create new series.");
      }
  };

  const handleSaveSeriesMeta = async () => {
      if (!editedSeriesName.trim() || !editedSeriesYear.trim()) {
          setErrorMessage("Series name and year cannot be empty.");
          return;
      }
      if (!selectedSeason) {
          setErrorMessage("No series selected to update.");
          return;
      }
      try {
          await updateDoc(doc(db, 'pointSeries', selectedSeason), { name: editedSeriesName, year: editedSeriesYear });
          setSeriesName(editedSeriesName);
          setErrorMessage("Series name updated successfully.");
          setIsEditNameModalOpen(false);
          // Refresh dropdown text
          setAllSeasons(prev => prev.map(s => s.id === selectedSeason ? { ...s, name: editedSeriesName, year: editedSeriesYear } : s).sort((a, b) => (b.year || '').localeCompare(a.year || '') || (a.name || '').localeCompare(b.name || '')));
      } catch (e) {
          logError("Error updating series metadata:", e);
          setErrorMessage("Failed to update series metadata.");
      }
  };

  const handleUpdateStatus = async () => {
      if (!selectedSeason) {
          setErrorMessage("No series selected to update.");
          return;
      }
      try {
          await updateDoc(doc(db, 'pointSeries', selectedSeason), { active: tempIsActive, archived: tempIsArchived });
          setIsActiveSeries(tempIsActive);
          setIsArchivedSeries(tempIsArchived);
          setErrorMessage("Series status updated successfully.");
          setIsUpdateStatusModalOpen(false);
      } catch (e) {
          logError("Error updating series status:", e);
          setErrorMessage("Failed to update series status.");
      }
  };


    /**
     * Handles populating the form with a player's entry data for editing.
     * @param {string} playerId - The ID of the player whose entry is being edited.
     * @param {number} entryWeekToEdit - The week number of the specific entry to edit.
     */
    const handleEditPlayerEntry = useCallback((playerId, entryWeekToEdit) => {
        const playerToEdit = players.find(p => p && p.id === playerId);
        if (playerToEdit) {
            const entryToEdit = playerToEdit.entries?.find(e => e && e.week === entryWeekToEdit);
            if (entryToEdit) {
                setPlayerName(playerToEdit.name);
                setPoints(entryToEdit.points.toString());
                setHighOut(entryToEdit.highOut === 'x' ? '' : entryToEdit.highOut.toString());
                setWeekNumber(entryToEdit.week.toString());
                setDnpChecked(entryToEdit.isDNP);

                // --- NEW: Populate partner info on edit ---
                const weekInfo = weekInfoEntries.find(wi => wi.week === entryToEdit.week);
                if (weekInfo?.doubles) {
                    let partner = null;
                    // First, try to find partner by explicit ID
                    if (entryToEdit.partnerId) {
                        partner = players.find(p => p.id === entryToEdit.partnerId);
                    } else {
                        // Fallback for old data: find partner by matching points in the same week
                        partner = players.find(p => p.id !== playerToEdit.id && p.entries.some(e => e.week === entryToEdit.week && e.points === entryToEdit.points));
                    }

                    if (partner) {
                        const partnerEntry = partner.entries.find(e => e.week === entryToEdit.week);
                        setPartnerName(partner.name);
                        setPartnerHighOut(partnerEntry ? (partnerEntry.highOut === 'x' ? '' : partnerEntry.highOut.toString()) : '');
                    }
                }
                setEditingPlayerInfo({ playerId: playerToEdit.id, originalEntryWeek: entryToEdit.week });
                setErrorMessage('');
                // Scroll to top to bring the form into view
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }, [players, weekInfoEntries]);

    const isDoublesWeek = useMemo(() => {
        if (!weekNumber) return false;
        const weekInfo = weekInfoEntries.find(wi => wi.week === parseInt(weekNumber));
        return weekInfo?.doubles === true;
    }, [weekNumber, weekInfoEntries]);

    /**
     * Handles cancelling an edit operation for Player Entry.
     */
    const handleCancelEdit = useCallback(() => {
        setPlayerName('');
        setPoints('');
        setHighOut('');
        setWeekNumber('');
        setDnpChecked(false);
        setPartnerName('');
        setPartnerHighOut('');
        setEditingPlayerInfo(null);
        setErrorMessage('');
    }, []);

    /**
     * Handles submitting the player entry form, either adding a new entry or updating an existing one.
     * This function now consolidates the logic for both "Add Player" and "Update Entry".
     */
    const handleSubmitEntry = useCallback(() => {
        const trimmedPlayerName = playerName.trim();
        if (!trimmedPlayerName) {
            setErrorMessage('Player Name cannot be empty.');
            return;
        }

        const parsedWeekNumber = weekNumber.trim() === '' ? null : parseInt(weekNumber);
        if (parsedWeekNumber === null || isNaN(parsedWeekNumber) || parsedWeekNumber < 1 || parsedWeekNumber > 12) {
            setErrorMessage('Week must be a number between 1 and 12.');
            return;
        }

        let currentPoints;
        if (dnpChecked) {
            currentPoints = 2;
        } else {
            const parsedPoints = parseInt(points);
            if (isNaN(parsedPoints)) {
                setErrorMessage('Points must be a valid number.');
                return;
            }
            currentPoints = parsedPoints;
        }

        const parsedHighOut = (highOut.trim() === '' || isNaN(parseInt(highOut))) ? 'x' : parseInt(highOut);

        // --- Doubles Logic ---
        if (isDoublesWeek) {
            const p1Name = playerName.trim();
            const p2Name = partnerName.trim();
            if (!p1Name || !p2Name) {
                setErrorMessage('For doubles, both player names are required.');
                return;
            }
            const parsedPartnerHighOut = (partnerHighOut.trim() === '' || isNaN(parseInt(partnerHighOut))) ? 'x' : parseInt(partnerHighOut);

            setPlayers(currentPlayers => {
                let workingPlayers = JSON.parse(JSON.stringify(Array.isArray(currentPlayers) ? currentPlayers.filter(Boolean) : []));

                // --- Handle Edits for Doubles ---
                if (editingPlayerInfo) {
                    const { playerId: originalPlayerId, originalEntryWeek } = editingPlayerInfo;
                    const originalPlayer = workingPlayers.find(p => p.id === originalPlayerId);
                    const originalEntry = originalPlayer?.entries.find(e => e.week === originalEntryWeek);
                    const originalPartnerId = originalEntry?.partnerId;

                    // Remove old entries for both original players
                    workingPlayers = workingPlayers.map(p => {
                        if (p.id === originalPlayerId || (originalPartnerId && p.id === originalPartnerId)) {
                            return { ...p, entries: p.entries.filter(e => e.week !== originalEntryWeek) };
                        }
                        return p;
                    });
                }

                let p1 = workingPlayers.find(p => p.name.toLowerCase() === p1Name.toLowerCase());
                if (!p1) {
                    p1 = { id: crypto.randomUUID(), name: p1Name, entries: [] };
                    workingPlayers.push(p1);
                }

                let p2 = workingPlayers.find(p => p.name.toLowerCase() === p2Name.toLowerCase());
                if (!p2) {
                    p2 = { id: crypto.randomUUID(), name: p2Name, entries: [] };
                    workingPlayers.push(p2);
                }

                if (!editingPlayerInfo && (p1.entries.some(e => e.week === parsedWeekNumber) || p2.entries.some(e => e.week === parsedWeekNumber))) {
                    setErrorMessage(`Error: An entry for week ${parsedWeekNumber} already exists for one of these players.`);
                    return currentPlayers; // Abort
                }

                const p1Entry = { id: crypto.randomUUID(), points: currentPoints, highOut: parsedHighOut, week: parsedWeekNumber, isDNP: dnpChecked, partnerId: p2.id, timestamp: Date.now() };
                const p2Entry = { id: crypto.randomUUID(), points: currentPoints, highOut: parsedPartnerHighOut, week: parsedWeekNumber, isDNP: dnpChecked, partnerId: p1.id, timestamp: Date.now() };

                p1.entries.push(p1Entry);
                p2.entries.push(p2Entry);

                return workingPlayers;
            });

            handleCancelEdit();
            return;
        }

        const newEntryData = {
            id: crypto.randomUUID(), // Ensure a unique ID for each entry
            points: currentPoints,
            highOut: parsedHighOut,
            week: parsedWeekNumber,
            isDNP: dnpChecked,
            partnerId: null, // Explicitly null for singles entries
            timestamp: Date.now() // Timestamp for the new/updated entry
        };

        setPlayers(currentPlayers => {
            let workingPlayers = Array.isArray(currentPlayers) ? currentPlayers.filter(Boolean) : [];

            if (editingPlayerInfo) {
                // ---- SCENARIO: Editing an existing entry ----
                const { playerId: originalPlayerId, originalEntryWeek } = editingPlayerInfo;

                // 1. Remove the original entry from its original player
                let originalPlayerHandled = false;
                workingPlayers = workingPlayers.map(p => {
                    if (p && p.id === originalPlayerId) {
                        originalPlayerHandled = true;
                        // Filter out the specific entry being edited
                        const filteredEntries = p.entries.filter(e => e && e.week !== originalEntryWeek);
                        return { ...p, entries: filteredEntries };
                    }
                    return p;
                }).filter(Boolean); // Filter out any empty player objects if needed

                if (!originalPlayerHandled) {
                    setErrorMessage('Error: Original player for editing not found. Data might be stale.');
                    return currentPlayers; // Abort and re-turn original state
                }

                // 2. Find or create the target player for the updated entry
                let targetPlayerIndex = workingPlayers.findIndex(p => p && p.name && p.name.toLowerCase() === trimmedPlayerName.toLowerCase());
                let targetPlayer;

                if (targetPlayerIndex > -1) {
                    // Player with current form name exists
                    targetPlayer = workingPlayers[targetPlayerIndex];
                } else {
                    // Player with current form name does not exist, create new player for it
                    targetPlayer = { id: crypto.randomUUID(), name: trimmedPlayerName, entries: [] };
                    workingPlayers.push(targetPlayer);
                    targetPlayerIndex = workingPlayers.length - 1; // Get the index of the newly added player
                }

                // 3. Check for week conflict at the target player's entries *before* adding the new entry
                const existingEntriesOfTargetPlayer = Array.isArray(targetPlayer?.entries) ? targetPlayer.entries.filter(Boolean) : [];
                const conflictIndex = existingEntriesOfTargetPlayer.findIndex(e => e && e.week === parsedWeekNumber);

                if (conflictIndex > -1) {
                    // Conflict detected: trying to update to a week that already exists for this (potentially new) player.
                    setErrorMessage(`Cannot update: Week ${parsedWeekNumber} already exists for ${trimmedPlayerName}.`);
                    // IMPORTANT: Revert the deletion of the original entry to prevent data loss on error
                    const originalPlayer = currentPlayers.find(p => p && p.id === originalPlayerId);
                    if (originalPlayer) {
                        const originalEntry = originalPlayer.entries.find(e => e && e.week === originalEntryWeek);
                        if (originalEntry) {
                            const currentOriginalPlayerInWorking = workingPlayers.find(p => p && p.id === originalPlayerId);
                            if (currentOriginalPlayerInWorking) {
                                currentOriginalPlayerInWorking.entries.push(originalEntry);
                                // Ensure entries remain sorted if needed for other logic
                                currentOriginalPlayerInWorking.entries.sort((a,b) => (a?.week ?? 0) - (b?.week ?? 0));
                            }
                        }
                    }
                    return currentPlayers; // Abort and return the state before attempted modification
                }

                // 4. Add the new entry data to the target player
                existingEntriesOfTargetPlayer.push(newEntryData);
                workingPlayers[targetPlayerIndex] = { ...targetPlayer, entries: existingEntriesOfTargetPlayer };

            } else {
                // ---- SCENARIO: Adding a brand new entry (no edit in progress) ----
                let targetPlayerIndex = workingPlayers.findIndex(p => p && p.name && p.name.toLowerCase() === trimmedPlayerName.toLowerCase());

                if (targetPlayerIndex > -1) {
                    // Player exists, check for duplicate week
                    const existingPlayer = workingPlayers[targetPlayerIndex];
                    const existingEntries = Array.isArray(existingPlayer?.entries) ? existingPlayer.entries.filter(Boolean) : [];
                    const weekEntryExistsIndex = existingEntries.findIndex(entry => entry && entry.week === parsedWeekNumber);

                    if (weekEntryExistsIndex > -1) {
                        // Duplicate week detected for new add, trigger overwrite modal
                        setOverwritePendingEntry({
                            playerName: trimmedPlayerName,
                            points: currentPoints,
                            highOut: parsedHighOut,
                            week: parsedWeekNumber,
                            isDNP: dnpChecked,
                            oldPoints: existingEntries[weekEntryExistsIndex].points,
                            oldHighOut: existingEntries[weekEntryExistsIndex].highOut,
                        });
                        setIsOverwriteModalModalOpen(true);
                        return currentPlayers; // Stop and wait for modal
                    } else {
                        // No duplicate, add new entry to existing player
                        existingEntries.push(newEntryData);
                        workingPlayers[targetPlayerIndex] = { ...existingPlayer, entries: existingEntries };
                    }
                } else {
                    // Player does not exist, create new player
                    const newPlayer = {
                        id: crypto.randomUUID(),
                        name: trimmedPlayerName,
                        entries: [newEntryData]
                    };
                    workingPlayers.push(newPlayer);
                }
            }
            return workingPlayers; // Return the modified array
        });

        // Clear form and editing state after successful submission
        setPlayerName('');
        setPoints('');
        setHighOut('');
        setWeekNumber(''); // Clear week number after submission
        setDnpChecked(false);
        setPartnerName('');
        setPartnerHighOut('');
        setEditingPlayerInfo(null); // Clear editing state after submission
        setErrorMessage('');
    }, [playerName, points, highOut, weekNumber, dnpChecked, editingPlayerInfo, handleCancelEdit, isDoublesWeek, partnerHighOut, partnerName]);

    /**
     * Confirms the overwrite of a duplicate week entry for a new entry.
     */
    const handleConfirmOverwrite = useCallback(() => {
        if (overwritePendingEntry) {
            setPlayers(currentPlayers => {
                const safeCurrentPlayers = Array.isArray(currentPlayers) ? currentPlayers.filter(Boolean) : [];
                const playerIndex = safeCurrentPlayers.findIndex(p => p && p.name && p.name.toLowerCase() === overwritePendingEntry.playerName.toLowerCase());
                if (playerIndex > -1) {
                    const updatedPlayers = [...safeCurrentPlayers];
                    const existingPlayer = updatedPlayers[playerIndex];
                    const existingEntries = Array.isArray(existingPlayer?.entries) ? existingPlayer.entries.filter(Boolean) : [];
                    const weekEntryExistsIndex = existingEntries.findIndex(entry => entry && entry.week === overwritePendingEntry.week);

                    if (weekEntryExistsIndex > -1) {
                        existingEntries[weekEntryExistsIndex] = {
                            id: existingEntries[weekEntryExistsIndex].id || crypto.randomUUID(),
                            points: overwritePendingEntry.points,
                            highOut: overwritePendingEntry.highOut,
                            week: overwritePendingEntry.week,
                            isDNP: overwritePendingEntry.isDNP,
                            timestamp: Date.now() // Update timestamp on overwrite
                        };
                        updatedPlayers[playerIndex] = { ...existingPlayer, entries: existingEntries };
                        return updatedPlayers;
                    }
                }
                return currentPlayers; // Should not happen if modal was shown
            });
            setOverwritePendingEntry(null);
            setIsOverwriteModalModalOpen(false);
            setPlayerName(''); // Clear form fields after successful overwrite
            setPoints('');
            setHighOut('');
            setWeekNumber('');
            setPartnerName('');
            setPartnerHighOut('');
            setDnpChecked(false);
            setErrorMessage('');
        }
    }, [overwritePendingEntry]);

    /**
     * Discards a new entry when a duplicate week is found.
     */
    const handleDiscardEntry = useCallback(() => {
        setOverwritePendingEntry(null);
        setErrorMessage('Entry discarded. No changes made.');
        setIsOverwriteModalModalOpen(false);
        setPlayerName(''); // Clear form fields after discard
        setPoints('');
        setHighOut('');
        setWeekNumber('');
        setPartnerName('');
        setPartnerHighOut('');
        setDnpChecked(false);
    }, []);

    /**
     * Handles changes to the DNP checkbox, setting points to 2 if checked.
     * @param {Object} e - The event object.
     */
    const handleDNPChange = useCallback((e) => {
        setDnpChecked(e.target.checked);
        setPoints(e.target.checked ? '2' : ''); // Set points to '2' or clear them
    }, []);

    /**
     * Handles removing a player from the list.
     * @param {string} playerIdToRemove - The ID of the player to remove.
     */
    const handleRemovePlayer = useCallback((playerIdToRemove) => {
        setPlayers(prevPlayers =>
            (Array.isArray(prevPlayers) ? prevPlayers.filter(Boolean) : []).filter(player => player && player.id !== playerIdToRemove)
        );
        setErrorMessage('');
    }, []);

    /**
     * Memoized array of players with aggregated stats (total points, highest out, weeks played) and rank.
     * Sorted by points (desc), high out (desc), weeks played (desc), then name (asc).
     */
    const sortedPlayers = useMemo(() => {
        const playersWithAggregatedData = (Array.isArray(players) ? players.filter(Boolean) : []).map(player => {
            // Defensive check for player object validity
            if (!player || typeof player.name !== 'string' || !Array.isArray(player.entries)) {
                console.warn('Invalid player object encountered in sortedPlayers memo:', player);
                return null;
            }

            const safeEntries = player.entries.filter(Boolean); // Filter out any null/undefined entries
            const totalPoints = safeEntries.reduce((sum, entry) => sum + (entry?.points ?? 0), 0);
            const highestOut = safeEntries.reduce((maxOut, entry) => {
                // Treat 'x' or invalid highOut as 0 for comparison within an individual player's highest out
                const currentHighOut = (entry?.highOut === 'x' || entry?.highOut === undefined || entry?.highOut === null || isNaN(entry?.highOut))
                    ? 0
                    : (typeof entry.highOut === 'string' ? parseInt(entry.highOut) : entry.highOut);

                // maxOut is already a number since it's initialized to 0 and only compared with numbers.
                return Math.max(maxOut, currentHighOut);
            }, 0); // Initialize accumulator with 0

            const totalWeeks = safeEntries.length;
            const isFinalist = totalPoints >= 29 || totalWeeks >= 6; // Adjusted threshold for lower points

            // New raffle entry calculations
            const hasRaffle6Plus = totalWeeks >= 6;
            const hasRaffle8Plus = totalWeeks >= 8;
            const hasRaffle10Plus = totalWeeks >= 10;

            return { ...player, totalPoints, highestOut, totalWeeks, isFinalist, hasRaffle6Plus, hasRaffle8Plus, hasRaffle10Plus };
        }).filter(Boolean); // Filter out any nulls resulting from invalid player objects

        const sorted = playersWithAggregatedData.sort((playerA, playerB) => {
            // Defensive checks before comparison
            if (!playerA || !playerB) return 0;

            // `highestOut` is now guaranteed to be a number (0 for 'x')
            const aHighOut = playerA.highestOut;
            const bHighOut = playerB.highestOut;

            // Sort by Points (descending)
            if (playerB.totalPoints !== playerA.totalPoints) {
                return playerB.totalPoints - playerA.totalPoints;
            }

            // Sort by High Out (descending, 'x' treated as 0)
            if (bHighOut !== aHighOut) {
                return bHighOut - aHighOut;
            }

            // Sort by Weeks of Play (descending)
            if (playerB.totalWeeks !== playerA.totalWeeks) {
                return playerB.totalWeeks - playerA.totalWeeks;
            }

            // Sort by Player Name (alphabetical ascending)
            return (playerA.name || '').localeCompare(playerB.name || '');
        });

        // Calculate ranks, handling ties
        let currentRank = 1;
        let prevTotalPoints = null;
        let prevHighOut = null;

        return sorted.map((player, index) => {
            // Defensive check
            if (!player) return null;

            // player.highestOut is already a number due to previous memoization
            const playerHighOutForRank = player.highestOut;

            // Update rank only if current player's points or high out are different from previous
            if (player.totalPoints !== prevTotalPoints || playerHighOutForRank !== prevHighOut) {
                currentRank = index + 1;
            }
            prevTotalPoints = player.totalPoints;
            prevHighOut = playerHighOutForRank;

            const isTop10 = currentRank <= 10;
            return { ...player, rank: currentRank, isTop10 };
        }).filter(Boolean);
    }, [players]);

    /**
     * Calculates the overall highest 'High Out' among all players, treating 'x' as 0.
     * Used to determine if a player has *the* highest out.
     * IMPORTANT: This now depends on `sortedPlayers` to ensure `highestOut` is always a number.
     */
    const overallHighestOut = useMemo(() => {
        let maxOut = 0; // Initialize with 0, as 'x' is treated as 0
        // Iterate over sortedPlayers because highestOut is already a number (0 if 'x') in these objects.
        (Array.isArray(sortedPlayers) ? sortedPlayers.filter(Boolean) : []).forEach(player => {
            if (typeof player.highestOut === 'number' && !isNaN(player.highestOut)) {
                if (player.highestOut > maxOut) {
                    maxOut = player.highestOut;
                }
            }
        });
        return maxOut;
    }, [sortedPlayers]); // Dependency is now sortedPlayers

    /**
     * Returns the name of the player with the highest 'High Out' score.
     * If there's a tie, it returns the name of the first player found with that score.
     * Returns 'N/A' if no player data or no valid high out.
     */
    const highestOutPlayerName = useMemo(() => {
        if (!Array.isArray(sortedPlayers) || sortedPlayers.length === 0) {
            return 'N/A';
        }

        const playerWithHighestOut = sortedPlayers.find(
            player => player && player.highestOut === overallHighestOut && overallHighestOut > 0
        );

        const nameToDisplay = playerWithHighestOut ? playerWithHighestOut.name : 'N/A';
        return nameToDisplay;
    }, [sortedPlayers, overallHighestOut]);

    /**
     * Calculates total entries (excluding DNP) and nightly payouts.
     */
    const { totalActualEntries, nightlyPayouts } = useMemo(() => {
        let actualEntriesCount = 0;
        (Array.isArray(players) ? players.filter(Boolean) : []).forEach(player => {
            (Array.isArray(player?.entries) ? player.entries.filter(Boolean) : []).forEach(entry => {
                if (entry && typeof entry.isDNP === 'boolean' && !entry.isDNP) {
                    actualEntriesCount++;
                }
            });
        });
        const payouts = actualEntriesCount * 5;
        return { totalActualEntries: actualEntriesCount, nightlyPayouts: payouts };
    }, [players]);

    /**
     * Calculates the total points awarded across all players and entries.
     */
    const totalPointsAwarded = useMemo(() => {
        let total = 0;
        (Array.isArray(players) ? players.filter(Boolean) : []).forEach(player => {
            (Array.isArray(player?.entries) ? player.entries.filter(Boolean) : []).forEach(entry => {
                total += (entry?.points ?? 0);
            });
        });
        return total;
    }, [players]);

    /**
     * Calculates the total number of finalists and the final payout.
     */
    const { totalFinalists, finalPayout } = useMemo(() => {
        const countFinalists = (Array.isArray(sortedPlayers) ? sortedPlayers.filter(Boolean) : []).filter(player => player?.isFinalist).length;
        const calculatedFinalPayout = totalActualEntries * 3;
        return { totalFinalists: countFinalists, finalPayout: calculatedFinalPayout };
    }, [sortedPlayers, totalActualEntries]);

    // NEW: Calculate total unique players
    const totalUniquePlayers = useMemo(() => {
        return players.length;
    }, [players]);

    /**
     * Memoized and sorted Mystery Out entries.
     */
    const sortedMysteryOutEntries = useMemo(() => {
        return (Array.isArray(mysteryOutEntries) ? mysteryOutEntries.filter(Boolean) : []).sort((a, b) => {
            // Defensive checks for entry existence and property types
            if (!a || !b) return 0;
            const weekA = a.week ?? 0;
            const weekB = b.week ?? 0;
            const timestampA = a.timestamp || 0;
            const timestampB = b.timestamp || 0;

            if (weekA !== weekB) return weekA - weekB;
            return timestampA - timestampB;
        });
    }, [mysteryOutEntries]);

    /**
     * Calculates Mystery Out wins, total payout for Mystery Out, and the current accumulated pot.
     */
    const { mysteryOutWins, mysteryOutTotalPayout, currentPotDisplay } = useMemo(() => {
        let winsCount = 0;
        let totalPayoutAmount = 0;
        let currentPotAccumulated = 0;

        const sortedEntries = (Array.isArray(mysteryOutEntries) ? mysteryOutEntries.filter(Boolean) : []).slice().sort((a, b) => {
            if (!a || !b) return 0;
            const weekA = a.week ?? 0;
            const weekB = b.week ?? 0;
            const timestampA = a.timestamp ?? 0;
            const timestampB = b.timestamp ?? 0;

            if (weekA !== weekB) return weekA - weekB;
            return timestampA - timestampB;
        });

        sortedEntries.forEach(entry => {
            if (!entry || typeof entry.player !== 'string') return;

            if (entry.player.toLowerCase() === 'x') {
                currentPotAccumulated += (entry.total ?? 0);
            } else {
                winsCount++;
                totalPayoutAmount += (entry.total ?? 0);
                currentPotAccumulated = 0; // Pot resets after a win
            }
        });
        return {
            mysteryOutWins: winsCount,
            mysteryOutTotalPayout: totalPayoutAmount,
            currentPotDisplay: currentPotAccumulated
        };
    }, [mysteryOutEntries]);

    /**
     * Calculates accumulated totals for Week Info categories.
     */
    const { totalDEWeeks, totalRRWeeks, totalSinglesWeeks, totalDoublesWeeks } = useMemo(() => {
        let deCount = 0;
        let rrCount = 0;
        let singlesCount = 0;
        let doublesCount = 0;

        (Array.isArray(weekInfoEntries) ? weekInfoEntries.filter(Boolean) : []).forEach(entry => {
            if (!entry) return;

            if (entry.doubleElim === true || entry.deChecked === true) deCount++;
            if (entry.roundRobin === true || entry.rrChecked === true) rrCount++;
            if (entry.singles === true) singlesCount++;
            if (entry.doubles === true) doublesCount++;
        });
        return { totalDEWeeks: deCount, totalRRWeeks: rrCount, totalSinglesWeeks: singlesCount, totalDoublesWeeks: doublesCount };
    }, [weekInfoEntries]);

    /**
     * Handles editing a Week Info entry by populating the form.
     * @param {string} entryId - The ID of the entry to edit.
     */
    const handleEditWeekInfo = useCallback((entryId) => {
        const entryToEdit = weekInfoEntries.find(e => e && e.id === entryId);
        if (entryToEdit) {
            setWeekInfoInputWeek(entryToEdit.week.toString());
            setNewDEChecked(entryToEdit.doubleElim === true || entryToEdit.deChecked === true);
            setNewRRChecked(entryToEdit.roundRobin === true || entryToEdit.rrChecked === true);
            setNewSinglesChecked(entryToEdit.singles);
            setNewDoublesChecked(entryToEdit.doubles);
            setNewFirstPlacePayout(entryToEdit.firstPlacePayout.toString());
            setNewSecondPlacePayout(entryToEdit.secondPlacePayout.toString());
            setNewThirdPlacePayout(entryToEdit.thirdPlacePayout.toString());
            setEditingWeekInfoId(entryId);
            setErrorMessage('');
            window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
        }
    }, [weekInfoEntries]);

    /**
     * Handles adding or updating a Week Info entry.
     */
    const handleAddWeekInfo = useCallback(() => {
        const parsedWeek = parseInt(weekInfoInputWeek);
        if (isNaN(parsedWeek) || parsedWeek < 1 || parsedWeek > 12) {
            setErrorMessage('Week for Week Info must be a number between 1 and 12.');
            return;
        }

        if ((newDEChecked && newRRChecked) || (!newDEChecked && !newRRChecked)) {
            setErrorMessage('Please select either Elimination Bracket OR Round Robin.');
            return;
        }

        if ((newSinglesChecked && newDoublesChecked) || (!newSinglesChecked && !newDoublesChecked)) {
            setErrorMessage('Please select either Singles OR Doubles.');
            return;
        }

        const firstPlace = parseFloat(newFirstPlacePayout) || 0;
        const secondPlace = parseFloat(newSecondPlacePayout) || 0;
        const thirdPlace = parseFloat(newThirdPlacePayout) || 0;

        setWeekInfoEntries(prevEntries => {
            const safePrevEntries = Array.isArray(prevEntries) ? prevEntries.filter(Boolean) : [];

            const originalEntry = safePrevEntries.find(entry => 
                editingWeekInfoId ? entry.id === editingWeekInfoId : entry.week === parsedWeek
            );
            let finalDate = originalEntry ? originalEntry.date : undefined;

            // If the date is missing (old data), calculate it based on Week 1's date.
            // This ensures that re-saving old entries corrects the data.
            if (!finalDate) {
                const weekOneInfo = safePrevEntries.find(e => e.week === 1);
                if (weekOneInfo && weekOneInfo.date) {
                    const weekOneDate = new Date(weekOneInfo.date.replace(/-/g, '/'));
                    const targetDate = new Date(weekOneDate.getTime());
                    targetDate.setDate(targetDate.getDate() + (parsedWeek - 1) * 7);
                    finalDate = targetDate.toLocaleDateString('en-US');
                }
            }

            const newEntry = {
                id: editingWeekInfoId || crypto.randomUUID(), // Use existing ID if editing, otherwise generate new
                week: parsedWeek,
                date: finalDate, // Use the calculated or preserved date
                doubleElim: newDEChecked,
                roundRobin: newRRChecked,
                singles: newSinglesChecked,
                doubles: newDoublesChecked,
                firstPlacePayout: firstPlace,
                secondPlacePayout: secondPlace,
                thirdPlacePayout: thirdPlace,
                timestamp: Date.now()
            };

            if (editingWeekInfoId) {
                // Update existing entry
                return safePrevEntries.map(entry =>
                    entry && entry.id === editingWeekInfoId ? newEntry : entry
                );
            } else {
                // Check for duplicate week for *new* entry
                const existingIndex = safePrevEntries.findIndex(entry => entry && entry.week === parsedWeek);
                if (existingIndex > -1) {
                    setErrorMessage(`Week ${parsedWeek} already has info. Please edit the existing entry or choose a different week.`);
                    return prevEntries; // Don't add, show error
                }
                // Add new entry
                return [...safePrevEntries, newEntry];
            }
        });
        setErrorMessage('');
        setEditingWeekInfoId(null); // Reset editing state
        // Clear form fields after adding/updating
        setWeekInfoInputWeek('');
        setNewDEChecked(false);
        setNewRRChecked(false);
        setNewSinglesChecked(false);
        setNewDoublesChecked(false);
        setNewFirstPlacePayout('');
        setNewSecondPlacePayout('');
        setNewThirdPlacePayout('');
    }, [weekInfoInputWeek, newDEChecked, newRRChecked, newSinglesChecked, newDoublesChecked, newFirstPlacePayout, newSecondPlacePayout, newThirdPlacePayout, editingWeekInfoId]);

    /**
     * Handles removing a Week Info entry by its ID.
     * @param {string} entryId - The ID of the entry to remove.
     */
    const handleRemoveWeekInfo = useCallback((entryId) => {
        setWeekInfoEntries(prevEntries =>
            (Array.isArray(prevEntries) ? prevEntries.filter(Boolean) : [])
                .filter(entry => entry && entry.id !== entryId)
        );
        // No errorMessage needed here as removal is generally a valid action
    }, []);

    /**
     * Handles cancelling an edit operation for Week Info.
     */
    const handleCancelEditWeekInfo = useCallback(() => {
        setWeekInfoInputWeek('');
        setNewDEChecked(false);
        setNewRRChecked(false);
        setNewSinglesChecked(false);
        setNewDoublesChecked(false);
        setNewFirstPlacePayout('');
        setNewSecondPlacePayout('');
        setNewThirdPlacePayout('');
        setEditingWeekInfoId(null);
        setErrorMessage('');
    }, []);


    // Determine the text for the main submission button
    const submitButtonText = editingPlayerInfo ? 'Update Entry' : 'Add Player';


    /**
     * Handles editing a Mystery Out entry by populating the form.
     * @param {string} entryId - The ID of the entry to edit.
     */
    const handleEditMysteryOut = useCallback((entryId) => {
        const entryToEdit = mysteryOutEntries.find(e => e && e.id === entryId);
        if (entryToEdit) {
            setMysteryOutWeek(entryToEdit.week.toString());
            setMysteryOutValue(entryToEdit.mysteryOut.toString());
            // When editing, if it was a player hit, we can set weekly contribution to the total.
            // If it was an 'x' entry, the weekly contribution *is* the total.
            // This is a pragmatic mapping, assuming the 'total' is what was contributed/awarded in that instance.
            setMysteryOutWeeklyContribution(entryToEdit.total.toString());
            setMysteryOutPlayer(entryToEdit.player);
            setEditingMysteryOutId(entryId);
            setErrorMessage('');
            window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
        }
    }, [mysteryOutEntries]);

    /**
     * Handles adding a new Mystery Out entry or updating an existing one.
     */
    const handleAddMysteryOut = useCallback(() => {
        const trimmedPlayer = mysteryOutPlayer.trim();
        const parsedWeek = parseInt(mysteryOutWeek);
        const parsedValue = parseInt(mysteryOutValue);
        const parsedWeeklyContribution = parseInt(mysteryOutWeeklyContribution);

        if (!trimmedPlayer) { setErrorMessage('Player name for Mystery Out cannot be empty.'); return; }
        if (isNaN(parsedWeek) || parsedWeek < 1 || parsedWeek > 12) { setErrorMessage('Week for Mystery Out must be a number between 1 and 12.'); return; }
        if (isNaN(parsedValue)) { setErrorMessage('Mystery Out value must be a valid number.'); return; }
        if (isNaN(parsedWeeklyContribution)) { setErrorMessage('Weekly Contribution for Mystery Out must be a valid number.'); return; }

        setMysteryOutEntries(prevEntries => {
            const safePrevEntries = Array.isArray(prevEntries) ? prevEntries.filter(Boolean) : [];
            let tempPotAccumulated = 0;

            // When editing, calculate the pot based on entries *excluding* the one being edited
            const entriesForPotCalculation = editingMysteryOutId
                ? safePrevEntries.filter(entry => entry && entry.id !== editingMysteryOutId).slice().sort((a,b) => (a?.week ?? 0) - (b?.week ?? 0) || (a?.timestamp ?? 0) - (b?.timestamp ?? 0))
                : safePrevEntries.slice().sort((a,b) => (a?.week ?? 0) - (b?.week ?? 0) || (a?.timestamp ?? 0) - (b?.timestamp ?? 0));

            entriesForPotCalculation.forEach(entry => {
                if (!entry || typeof entry.player !== 'string') return;
                if (entry.player.toLowerCase() === 'x') {
                    tempPotAccumulated += (entry.total ?? 0);
                } else {
                    tempPotAccumulated = 0;
                }
            });

            let totalAwardedForThisEntry;
            if (trimmedPlayer.toLowerCase() === 'x') {
                totalAwardedForThisEntry = parsedWeeklyContribution;
            } else {
                totalAwardedForThisEntry = tempPotAccumulated + parsedWeeklyContribution;
            }

            const newMysteryOutEntry = {
                id: editingMysteryOutId || crypto.randomUUID(), // Use existing ID if editing, otherwise generate new
                week: parsedWeek,
                mysteryOut: parsedValue,
                total: totalAwardedForThisEntry, // This is the calculated total based on pot and weekly contribution
                player: trimmedPlayer,
                timestamp: Date.now()
            };

            if (editingMysteryOutId) {
                // Update existing entry
                return safePrevEntries.map(entry =>
                    entry && entry.id === editingMysteryOutId ? newMysteryOutEntry : entry
                );
            } else {
                // Add new entry
                return [...safePrevEntries, newMysteryOutEntry];
            }
        });

        // Reset inputs after adding/updating
        setMysteryOutWeek('');
        setMysteryOutValue('');
        setMysteryOutWeeklyContribution('');
        setMysteryOutPlayer('');
        setEditingMysteryOutId(null); // Reset editing state
        setErrorMessage('');
    }, [mysteryOutWeek, mysteryOutValue, mysteryOutWeeklyContribution, mysteryOutPlayer, editingMysteryOutId]);

    /**
     * Handles removing a Mystery Out entry by its ID.
     * @param {string} entryId - The ID of the entry to remove.
     */
    const handleRemoveMysteryOut = useCallback((entryId) => {
        setMysteryOutEntries(prevEntries =>
            (Array.isArray(prevEntries) ? prevEntries.filter(Boolean) : [])
                .filter(entry => entry && entry.id !== entryId)
        );
        // No errorMessage needed here
    }, []);

    /**
     * Handles cancelling an edit operation for Mystery Out.
     */
    const handleCancelEditMysteryOut = useCallback(() => {
        setMysteryOutWeek('');
        setMysteryOutValue('');
        setMysteryOutWeeklyContribution('');
        setMysteryOutPlayer('');
        setEditingMysteryOutId(null);
        setErrorMessage('');
    }, []);
    // --- End of Mystery Out Handlers ---

    const handleNormalizeAllPointSeriesDates = useCallback(async () => {
        if (!window.confirm("This will scan ALL point series documents and convert date formats from YYYY-MM-DD to MM/DD/YYYY. This is a one-time operation. Are you sure?")) {
            return;
        }
        setErrorMessage("Normalizing all point series dates...");
        try {
            const querySnapshot = await getDocs(collection(db, 'pointSeries'));
            let updatedCount = 0;
            const promises = [];

            for (const docSnap of querySnapshot.docs) {
                const seriesData = docSnap.data();
                const weekInfo = seriesData.weekInfo || [];
                let needsUpdate = false;
                const newWeekInfo = weekInfo.map(wi => {
                    if (wi.date && /^\d{4}-\d{2}-\d{2}$/.test(wi.date)) {
                        needsUpdate = true;
                        // Correctly parse YYYY-MM-DD as local time to avoid timezone shifts
                        const newDate = new Date(wi.date.replace(/-/g, '/')).toLocaleDateString('en-US');
                        return { ...wi, date: newDate };
                    }
                    return wi;
                });

                if (needsUpdate) {
                    promises.push(updateDoc(doc(db, 'pointSeries', docSnap.id), { weekInfo: newWeekInfo }));
                    updatedCount++;
                }
            }

            if (promises.length > 0) {
                await Promise.all(promises);
            }

            setErrorMessage(`Date normalization complete. ${updatedCount} series documents were updated.`);
            // Optionally, reload current season data if it was affected
            if (selectedSeason && promises.length > 0) {
                // You might want to trigger a re-fetch of the current season's data here
            }
        } catch (e) {
            logError("Error normalizing dates:", e);
            setErrorMessage("An error occurred during date normalization. Check console.");
        }
    }, [selectedSeason]);

    const handleRunMigration = useCallback(async () => {
        if (!selectedSeason || !migrationStartDate || !migrationDayOfWeek) {
            alert('Please select a series, a start date, and a recurring day for the migration.');
            return;
        }
        if (!window.confirm('This will permanently modify the data for the selected series, renaming fields and adding dates. Are you sure you want to proceed?')) {
            return;
        }

        try {
            setErrorMessage('Migration in progress...');
            const seriesDocRef = doc(db, 'pointSeries', selectedSeason);
            const seriesDoc = await getDoc(seriesDocRef);

            if (!seriesDoc.exists()) {
                setErrorMessage('Error: Could not find the selected series to migrate.');
                return;
            }

            const seriesData = seriesDoc.data();
            const oldWeekInfo = seriesData.weekInfo || [];

            const getNextDayOfWeek = (date, dayOfWeek) => {
                const resultDate = new Date(date); // Safer date parsing
                const currentDay = resultDate.getUTCDay();
                const distance = (dayOfWeek - currentDay + 7) % 7;
                resultDate.setUTCDate(resultDate.getUTCDate() + distance);
                return resultDate;
            };

            const weekOneDate = getNextDayOfWeek(new Date(migrationStartDate.replace(/-/g, '/')), parseInt(migrationDayOfWeek, 10));

            const finalMigratedWeekInfo = oldWeekInfo.map(week => {
                const weekDate = new Date(weekOneDate.getTime());
                weekDate.setDate(weekDate.getDate() + ((week.week - 1) * 7));

                const newWeek = { ...week, date: weekDate.toLocaleDateString('en-US'), roundRobin: week.rrChecked || false, doubleElim: week.deChecked || false, };
                delete newWeek.rrChecked;
                delete newWeek.deChecked;
                return newWeek;
            });

            await updateDoc(seriesDocRef, { weekInfo: finalMigratedWeekInfo });

            setWeekInfoEntries(finalMigratedWeekInfo); // Update local state
            setErrorMessage('Migration successful! Data has been updated.');
        } catch (e) {
            logError('Error during migration:', e);
            setErrorMessage('An error occurred during migration. Check the console.');
        }
    }, [selectedSeason, migrationStartDate, migrationDayOfWeek]);

    const handleSaveChanges = useCallback(async () => {
        if (!selectedSeason) {
            alert('Please select a series to save changes to.');
            return;
        }
        try {
            const ref = doc(db, 'pointSeries', selectedSeason);
            const currentSeriesMeta = allSeasons.find(s => s.id === selectedSeason);
            const payload = {
                name: currentSeriesMeta?.name || seriesName,
                year: currentSeriesMeta?.year || new Date().getFullYear().toString(),
                active: isActiveSeries,
                archived: isArchivedSeries,
                // Use server timestamp for consistency, but only on create, not every update
                updatedAt: serverTimestamp(),
                players: sortedPlayers, // Publish the full sorted players data
                weekInfo: Array.isArray(weekInfoEntries) ? weekInfoEntries.filter(Boolean) : [],
                mysteryOut: Array.isArray(mysteryOutEntries) ? mysteryOutEntries.filter(Boolean) : [],
                summary: {
                    totalEntries: totalActualEntries || 0,
                    uniquePlayers: totalUniquePlayers || 0,
                    pointsAwarded: totalPointsAwarded || 0,
                    nightlyPayouts: nightlyPayouts || 0,
                    totals: {
                        deWeeks: totalDEWeeks || 0,
                        rrWeeks: totalRRWeeks || 0,
                        singlesWeeks: totalSinglesWeeks || 0,
                        doublesWeeks: totalDoublesWeeks || 0,
                        finalists: totalFinalists || 0,
                        finalPayout: finalPayout || 0,
                        overallHighestOut: overallHighestOut || 0,
                        highestOutPlayerName: (highestOutPlayerName || 'N/A'),
                    }
                }
            };
            await updateDoc(ref, payload);
            alert('Series changes have been saved to Firestore.');
        } catch (e) {
            logError(`Error publishing series ${selectedSeason}:`, e);
            alert('Failed to publish series. See console for details.');
        }
    }, [selectedSeason, allSeasons, seriesName, isActiveSeries, isArchivedSeries, sortedPlayers, weekInfoEntries, mysteryOutEntries, totalActualEntries, totalUniquePlayers, totalPointsAwarded, nightlyPayouts, totalDEWeeks, totalRRWeeks, totalSinglesWeeks, totalDoublesWeeks, totalFinalists, finalPayout, overallHighestOut, highestOutPlayerName]);

    const rawLoadedData = useMemo(() => {
        if (!selectedSeason) return null;
        const currentSeriesMeta = allSeasons.find(s => s.id === selectedSeason);
        return {
            id: selectedSeason,
            name: currentSeriesMeta?.name,
            year: currentSeriesMeta?.year,
            active: isActiveSeries,
            archived: isArchivedSeries,
            players,
            weekInfo: weekInfoEntries,
            mysteryOut: mysteryOutEntries,
        };
    }, [selectedSeason, allSeasons, isActiveSeries, isArchivedSeries, players, weekInfoEntries, mysteryOutEntries]);
    
    return (
        <div className="min-h-screen p-4 sm:p-6 lg:p-8 flex items-start justify-center relative">
            {/* Theme palette control is handled globally; remove dark-mode toggle. */}

            <div className="card rounded-xl shadow-2xl p-6 sm:p-8 lg:p-10 w-full max-w-4xl">
                <h1 className="page-title heading-primary-1">Leaderboard</h1>

                {/* Series Meta & Publish Controls */}
                <section className="mb-6 p-4 surface-low rounded-lg border border-low">
                    <h3 className="text-lg font-semibold text-strong mb-3">Series Management</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label className="block text-xs font-medium text-muted">Load Existing Season</label>
                            <select value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)} className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low" aria-label="Load Existing Season">
                                <option value="">-- Select a Season --</option>
                                {allSeasons.map(s => <option key={s.id} value={s.id}>{s.name} - {s.year}</option>)}
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => { const current = allSeasons.find(s => s.id === selectedSeason); setEditedSeriesName(current?.name || ''); setEditedSeriesYear(current?.year || ''); setIsEditNameModalOpen(true); }} className="btn btn-secondary w-full" disabled={!selectedSeason}>
                                Edit
                            </button>
                            <button onClick={() => { setTempIsActive(isActiveSeries); setTempIsArchived(isArchivedSeries); setIsUpdateStatusModalOpen(true); }} className="btn btn-secondary w-full" disabled={!selectedSeason}>
                                Update Status
                            </button>
                        </div>
                        <div>
                            <button onClick={() => setIsCreateSeriesModalOpen(true)} className="btn btn-success w-full">
                                Create New Series
                            </button>                        </div>
                    </div>
                </section>

                {/* Week Info Input Section */}
                <section className="mb-8 p-4 sm:p-6 surface-low rounded-lg border border-low">
                    <h2 className="text-xl sm:text-2xl font-semibold text-strong mb-5">Add Week Info</h2>
                    <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* Week Dropdown for Week Info */}
                                <div>
                                    <label htmlFor="weekInfoInputWeek" className="block text-sm font-medium text-muted mb-1">
                                        Week
                                    </label>
                                    <select
                                        id="weekInfoInputWeek"
                                        value={weekInfoInputWeek}
                                        onChange={(e) => setWeekInfoInputWeek(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                    >
                                        <option value="">Select Week</option>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(week => (
                                            <option key={`week-info-week-${week}`} value={week}>Week {week}</option>
                                        ))}
                                    </select>
                                </div>
                                {/* Tournament Type Radios */}
                                <div className="mt-1 flex items-center space-x-4">
                                    <label className="block text-sm font-medium text-muted">Tournament Type</label>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="newDEChecked"
                                            name="eliminationType"
                                            checked={newDEChecked}
                                            onChange={() => { setNewDEChecked(true); setNewRRChecked(false); }}
                                            className="h-4 w-4"
                                        />
                                        <label htmlFor="newDEChecked" className="ml-2 block text-sm">
                                            Elimination Bracket
                                        </label>
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="newRRChecked"
                                            name="eliminationType"
                                            checked={newRRChecked}
                                            onChange={() => { setNewRRChecked(true); setNewDEChecked(false); }}
                                            className="h-4 w-4"
                                        />
                                        <label htmlFor="newRRChecked" className="ml-2 block text-sm">
                                            Round Robin
                                        </label>
                                    </div>
                                </div>
                                {/* Game Type Radios */}
                                <div className="mt-1 flex items-center space-x-4">
                                    <label className="block text-sm font-medium text-muted">Game Type</label>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="newSinglesChecked"
                                            name="gameType"
                                            checked={newSinglesChecked}
                                            onChange={() => { setNewSinglesChecked(true); setNewDoublesChecked(false); }}
                                            className="h-4 w-4"
                                        />
                                        <label htmlFor="newSinglesChecked" className="ml-2 block text-sm">
                                            Singles
                                        </label>
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="newDoublesChecked"
                                            name="gameType"
                                            checked={newDoublesChecked}
                                            onChange={() => { setNewDoublesChecked(true); setNewSinglesChecked(false); }}
                                            className="h-4 w-4"
                                        />
                                        <label htmlFor="newDoublesChecked" className="ml-2 block text-sm">
                                            Doubles
                                        </label>
                                    </div>
                                </div>
                                {/* Payouts */}
                                <div>
                                    <label htmlFor="newFirstPlacePayout" className="block text-sm font-medium text-muted mb-1">
                                        1st Place Payout ($)
                                    </label>
                                    <input
                                        type="number"
                                        id="newFirstPlacePayout"
                                        value={newFirstPlacePayout}
                                        onChange={(e) => setNewFirstPlacePayout(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="newSecondPlacePayout" className="block text-sm font-medium text-muted mb-1">
                                        2nd Place Payout ($)
                                    </label>
                                    <input
                                        type="number"
                                        id="newSecondPlacePayout"
                                        value={newSecondPlacePayout}
                                        onChange={(e) => setNewSecondPlacePayout(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 50"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="newThirdPlacePayout" className="block text-sm font-medium text-muted mb-1">
                                        3rd Place Payout ($)
                                    </label>
                                    <input
                                        type="number"
                                        id="newThirdPlacePayout"
                                        value={newThirdPlacePayout}
                                        onChange={(e) => setNewThirdPlacePayout(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 25"
                                    />
                                </div>
                            </div>
                            {errorMessage && (
                                <p className="text-danger text-center text-sm mb-4">{errorMessage}</p>
                            )}
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleAddWeekInfo} // This should be handleAddWeekInfo
                                    className="btn btn-primary flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                >
                                    {editingWeekInfoId ? 'Update Week Info' : 'Add Week Info'}
                                </button>
                                {editingWeekInfoId && (
                                    <button
                                        onClick={handleCancelEditWeekInfo}
                                        className="btn btn-secondary flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </div>


                            {/* Week Info Entries Table */}
                            <div className="mt-8 overflow-x-auto rounded-lg shadow-md border border-low">
                                <h3 className="text-lg font-semibold text-strong mb-2 text-center p-4">Recorded Week Info</h3>
                                <table className="min-w-full">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Week</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Elimination Bracket</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Round Robin</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Singles</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Doubles</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">1st Place Payout</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">2nd Place Payout</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">3rd Place Payout</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="row-alt">
                                        {Array.isArray(weekInfoEntries) && weekInfoEntries.length > 0 ? (
                                            weekInfoEntries.filter(Boolean).sort((a,b) => (a?.week ?? 0) - (b?.week ?? 0)).map((entry) => (
                                                <tr key={entry.id ?? `weekinfo-${entry.week}`} className="border-b border-low hover:opacity-95 text-center">
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">{entry.week ?? ''}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">{entry.doubleElim ? 'Yes' : 'No'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">{entry.roundRobin ? 'Yes' : 'No'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{entry.singles ? 'Yes' : 'No'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{entry.doubles ? 'Yes' : 'No'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">${entry.firstPlacePayout ?? 0}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">${entry.secondPlacePayout ?? 0}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">${entry.thirdPlacePayout ?? 0}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                        <div className="flex items-center justify-center space-x-2">
                                                            <button
                                                                onClick={() => handleEditWeekInfo(entry.id)}
                                                                className="btn btn-info py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveWeekInfo(entry.id)}
                                                                className="btn btn-danger py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="px-4 py-8 text-center text-muted italic">
                                                    No Week Info entries yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                    </>
                </section>

                {/* Add New Player Form Section */}
                <section className="mb-8 p-4 sm:p-6 surface-low rounded-lg border border-low">
                    <h2 className="text-xl sm:text-2xl font-semibold text-strong mb-5">
                        {editingPlayerInfo ? 'Edit Player Entry' : 'Add New Player Entry'}
                    </h2>
                    <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Player Name Input */}
                                <div>
                                    <label htmlFor="playerName" className="block text-sm font-medium text-muted mb-1">
                                        Player Name
                                    </label>
                                    <input
                                        type="text"
                                        id="playerName"
                                        value={playerName}
                                        onChange={(e) => setPlayerName(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., John Doe"
                                    />
                                </div>
                                {/* Points Input */}
                                <div>
                                    <label htmlFor="points" className="block text-sm font-medium text-muted mb-1">
                                        Points
                                    </label>
                                    <input
                                        type="number"
                                        id="points"
                                        value={points}
                                        onChange={(e) => setPoints(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 1500"
                                        disabled={dnpChecked}
                                    />
                                </div>
                                {/* High Out Input */}
                                <div>
                                    <label htmlFor="highOut" className="block text-sm font-medium text-muted mb-1">
                                        High Out (Leave empty for 'x')
                                    </label>
                                    <input
                                        type="text"
                                        id="highOut"
                                        value={highOut}
                                        onChange={(e) => setHighOut(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 170 or leave empty"
                                    />
                                </div>
                                {/* Week Number Dropdown */}
                                <div>
                                    <label htmlFor="weekNumber" className="block text-sm font-medium text-muted mb-1">
                                        Week
                                    </label>
                                    <select
                                        id="weekNumber"
                                        value={weekNumber}
                                        onChange={(e) => setWeekNumber(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                    >
                                        <option value="">Select Week</option>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(week => (
                                            <option key={`week-${week}`} value={week}>Week {week}</option>
                                        ))}
                                    </select>
                                </div>
                                {isDoublesWeek && (
                                    <div className="col-span-1 md:col-span-2 border-t border-dashed border-low mt-4 pt-4">
                                        <h3 className="text-md font-semibold text-strong mb-2">Partner Details</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="partnerName" className="block text-sm font-medium text-muted mb-1">Partner Name</label>
                                                <input type="text" id="partnerName" value={partnerName} onChange={e => setPartnerName(e.target.value)} className="input-field w-full" placeholder="Partner's Name" />
                                            </div>
                                            <div>
                                                <label htmlFor="partnerHighOut" className="block text-sm font-medium text-muted mb-1">Partner High Out</label>
                                                <input type="text" id="partnerHighOut" value={partnerHighOut} onChange={e => setPartnerHighOut(e.target.value)} className="input-field w-full" placeholder="Leave empty for 'x'" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* DNP Checkbox */}
                            <div className="flex items-center mb-4">
                                <input
                                    type="checkbox"
                                    id="dnpCheckbox"
                                    checked={dnpChecked}
                                    onChange={handleDNPChange}
                                    className="h-4 w-4"
                                />
                                <label htmlFor="dnpCheckbox" className="ml-2 block text-sm">
                                    DNP (Did Not Play) - Sets points to 2
                                </label>
                            </div>

                            {errorMessage && (
                                <p className="text-danger text-center text-sm mb-4">{errorMessage}</p>
                            )}
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleSubmitEntry}
                                    className="btn btn-primary flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                >
                                    {submitButtonText}
                                </button>
                                {editingPlayerInfo && (
                                    <button
                                        onClick={handleCancelEdit}
                                        className="btn btn-secondary flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </div>
                    </>
                </section>

                <h2 className="text-2xl font-bold text-strong mb-4 text-center p-4">Summer Point Series Results</h2>
                {/* Main Leaderboard Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 px-4">
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Total Entries</div>
                        <div className="text-xl font-bold text-strong">{totalActualEntries}</div>
                    </div>
                    {/* Unique Players Display */}
                    <div className="bg-info-soft p-4 rounded-lg text-center">
                        <div className="text-base font-semibold text-info">Unique Players</div>
                        <div className="text-xl font-bold text-strong">{totalUniquePlayers}</div>
                    </div>
                    {/* Points Awarded Display */}
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Points Awarded</div>
                        <div className="text-xl font-bold text-strong">{totalPointsAwarded}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Nightly Payouts</div>
                        <div className="text-xl font-bold text-strong">${nightlyPayouts}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Elimination Brackets</div>
                        <div className="text-xl font-bold text-strong">{totalDEWeeks}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Round Robins</div>
                        <div className="text-xl font-bold text-strong">{totalRRWeeks}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Singles</div>
                        <div className="text-xl font-bold text-strong">{totalSinglesWeeks}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Doubles</div>
                        <div className="text-xl font-bold text-strong">{totalDoublesWeeks}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Finalists</div>
                        <div className="text-xl font-bold text-strong">{totalFinalists}</div>
                    </div>
                    <div className="surface-low p-4 rounded-lg text-center border border-low">
                        <div className="text-base font-semibold text-muted">Final Payout</div>
                        <div className="text-xl font-bold text-strong">${finalPayout}</div>
                    </div>
                    {/* Highest Out Player Display */}
                    <div className="bg-primary-soft p-4 rounded-lg text-center lg:col-span-2">
                        <div className="text-base font-semibold subheading-primary-1">Highest Out Player</div>
                        <div className="text-xl font-bold text-strong inline-flex items-center justify-center">
                            {highestOutPlayerName}
                            {highestOutPlayerName !== 'N/A' && overallHighestOut > 0 && (
                                <span className="ml-1.5 px-1.5 py-0.25 soft-primary subheading-primary-1 text-[0.6rem] font-semibold rounded-md shadow-sm">
                                    High Out
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Player Table Section */}
                <section className="overflow-x-auto rounded-lg shadow-md border border-low mt-8">
                    <h1 className="text-2xl font-bold text-strong mb-4 text-center p-4">Leaderboard</h1>
                    <table className="min-w-full">
                        <thead className="table-header">
                            <tr>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Rank
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                                    Player
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Points
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    High Out
                                </th>
                                {/* Changed column title for responsive display */}
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Weeks<span className="md:inline hidden"> of Play</span><span className="md:hidden"></span>
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="row-alt">
                            {Array.isArray(sortedPlayers) && sortedPlayers.length > 0 ? (
                                sortedPlayers.map((player, index) => {
                                    if (!player) return null;
                                    const rank = player.rank ?? (index + 1);
                                    const borderClass = (rank === 3 || rank === 6 || rank === 10) ? 'border-b-2 border-low' : '';

                                    // Determine if this player has the overall highest out
                                    const isHighestOutPlayer = (player.highestOut === overallHighestOut && player.highestOut > 0);

                                    return (
                                        <tr
                                            key={player.id ?? `player-${index}`}
                                            className={`border-b border-low cursor-pointer hover:opacity-95 ${player.isTop10 ? 'soft-success' : ''} ${borderClass}`}
                                            onClick={() => {
                                                setIsEntriesModalOpen(true);
                                                setSelectedPlayer(player);
                                            }}
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-center min-h-[95px]">
                                                {rank}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-left responsive-player-name-cell min-h-[95px]">
                                                <div className="flex flex-col items-start gap-y-1"> {/* Changed to flex-col for name + tags stacking within the cell */}
                                                    <span className="font-medium flex-shrink-0">{player.name ?? ''}</span>
                                                    <div className="flex flex-row flex-wrap items-center gap-x-1 gap-y-1">
                                                        {player.isFinalist && (
                                                            <span className="inline-block px-1.5 py-0.25 bg-success-soft text-success text-[0.6rem] font-semibold rounded-md shadow-sm">
                                                                Finalist
                                                            </span>
                                                        )}
                                                        {player.hasRaffle6Plus && (
                                                            <span className="inline-block px-1.5 py-0.25 bg-info-soft text-info text-[0.6rem] font-semibold rounded-md shadow-sm">
                                                                6+
                                                            </span>
                                                        )}
                                                        {player.hasRaffle8Plus && (
                                                            <span className="inline-block px-1.5 py-0.25 bg-warning-soft text-warning text-[0.6rem] font-semibold rounded-md shadow-sm">
                                                                8+
                                                            </span>
                                                        )}
                                                        {player.hasRaffle10Plus && (
                                                            <span className="inline-block px-1.5 py-0.25 bg-warning-soft text-warning text-[0.6rem] font-semibold rounded-md shadow-sm">
                                                                10+
                                                            </span>
                                                        )}
                                                        {isHighestOutPlayer && (
                                                            <span className="inline-block px-1.5 py-0.25 soft-primary subheading-primary-1 text-[0.6rem] font-semibold rounded-md shadow-sm">
                                                                High Out
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center min-h-[95px]">
                                                {player.totalPoints ?? 0}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center min-h-[95px]">
                                                {player.highestOut === 0 ? 'x' : (player.highestOut ?? 'x')}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center min-h-[95px]">
                                                {player.totalWeeks ?? 0}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center min-h-[95px]">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent modal from opening
                                                            setIsEntriesModalOpen(true);
                                                            setSelectedPlayer(player);
                                                        }}
                                                        className="btn btn-info py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent modal from opening
                                                            handleRemovePlayer(player.id);
                                                        }}
                                                        className="btn btn-danger py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-muted italic">
                                        No players added yet. Add a player or import a table above!
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>

                {/* Mystery Out Section */}
                <section className="mt-8 p-4 sm:p-6 surface-low rounded-lg border border-low">
                    <h2 className="text-xl sm:text-2xl font-semibold text-strong mb-5">Add Mystery Out</h2>
                    <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Week Input */}
                                <div>
                                    <label htmlFor="mysteryOutWeek" className="block text-sm font-medium text-muted mb-1">
                                        Week
                                    </label>
                                    <select
                                        id="mysteryOutWeek"
                                        value={mysteryOutWeek}
                                        onChange={(e) => setMysteryOutWeek(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                    >
                                        <option value="">Select Week</option>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(week => (
                                            <option key={`mo-week-${week}`} value={week}>Week {week}</option>
                                        ))}
                                    </select>
                                </div>
                                {/* Mystery Out Value Input */}
                                <div>
                                    <label htmlFor="mysteryOutValue" className="block text-sm font-medium text-muted mb-1">
                                        Mystery Out
                                    </label>
                                    <input
                                        type="number"
                                        id="mysteryOutValue"
                                        value={mysteryOutValue}
                                        onChange={(e) => setMysteryOutValue(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 170"
                                    />
                                </div>
                                {/* Weekly Contribution Input */}
                                <div>
                                    <label htmlFor="mysteryOutWeeklyContribution" className="block text-sm font-medium text-muted mb-1">
                                        Weekly Contribution
                                    </label>
                                    <input
                                        type="number"
                                        id="mysteryOutWeeklyContribution"
                                        value={mysteryOutWeeklyContribution}
                                        onChange={(e) => setMysteryOutWeeklyContribution(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., 50"
                                    />
                                </div>
                                {/* Player Input */}
                                <div>
                                    <label htmlFor="mysteryOutPlayer" className="block text-sm font-medium text-muted mb-1">
                                        Player
                                    </label>
                                    <input
                                        type="text"
                                        id="mysteryOutPlayer"
                                        value={mysteryOutPlayer}
                                        onChange={(e) => setMysteryOutPlayer(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 rounded-md border border-low surface-low"
                                        placeholder="e.g., Jane Doe or x"
                                    />
                                </div>
                            </div>
                            {errorMessage && (
                                <p className="text-danger text-center text-sm mb-4">{errorMessage}</p>
                            )}
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleAddMysteryOut}
                                    className="btn btn-success flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                >
                                    {editingMysteryOutId ? 'Update Mystery Out Entry' : 'Add Mystery Out Entry'}
                                </button>
                                {editingMysteryOutId && (
                                    <button
                                        onClick={handleCancelEditMysteryOut}
                                        className="btn btn-secondary flex-1 py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </div>
                    </>
                </section>

                {/* Mystery Out Table Section */}
                <section className="overflow-x-auto rounded-lg shadow-md border border-low mt-8">
                    <h2 className="text-2xl font-bold text-strong mb-4 text-center p-4">Mystery Out Results</h2>
                    {/* Display Mystery Out Wins, Total Payout, and Current Pot in a grid */}
                    <div className="grid grid-cols-3 gap-4 mb-4 px-4"> {/* Changed to grid-cols-3 for all views */}
                        <div className="surface-low p-4 rounded-lg text-center border border-low">
                            <div className="text-base font-semibold text-muted">Number of Wins</div>
                            <div className="text-xl font-bold text-strong">{mysteryOutWins}</div>
                        </div>
                        <div className="surface-low p-4 rounded-lg text-center border border-low">
                            <div className="text-base font-semibold text-muted">Total Payouts</div>
                            <div className="text-xl font-bold text-strong">${mysteryOutTotalPayout}</div>
                        </div>
                        
                        <div className="bg-info-soft p-4 rounded-lg text-center">
                            <div className="text-base font-semibold text-info">Current Pot</div>
                            <div className="text-xl font-bold text-strong">${currentPotDisplay}</div>
                        </div>
                    </div>
                    <table className="min-w-full">
                        <thead className="table-header">
                            <tr>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Week
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Mystery Out
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Total
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                                    Player
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="row-alt">
                            {Array.isArray(sortedMysteryOutEntries) && sortedMysteryOutEntries.length > 0 ? (
                                sortedMysteryOutEntries.map((entry, index) => {
                                    if (!entry) return null;
                                    return (
                                        <tr key={entry.id ?? `mystery-${index}`} className="border-b border-low hover:opacity-95">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                {entry.week ?? ''}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                {entry.mysteryOut ?? ''}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                ${entry.total ?? 0}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-left">
                                                {entry.player ?? ''}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleEditMysteryOut(entry.id)}
                                                        className="btn btn-info py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveMysteryOut(entry.id)}
                                                        className="btn btn-danger py-1 px-3 rounded-md text-xs transition duration-200 ease-in-out"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-muted italic">
                                        No Mystery Out entries yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>

                {/* Download Buttons Section */}
                <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-8 border-t border-low pt-6">
                    <button
                        onClick={handleSaveChanges}
                        className="btn btn-primary w-full sm:w-auto py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
                        disabled={!selectedSeason}
                    >
                        Save All Changes to Firestore
                    </button>
                </div>

                {/* One-Time Data Tools */}
                <div className="mt-8 p-4 border-2 border-dashed border-info rounded-lg">
                    <h4 className="text-info font-bold mb-2">One-Time Data Tools</h4>
                    <p className="text-sm text-muted mb-4">Run these tools to update data structures across all documents. Use with caution.</p>
                    <div className="flex gap-4">
                        <button onClick={handleNormalizeAllPointSeriesDates} className="btn btn-info">
                            Normalize All Point Series Dates
                        </button>
                    </div>
                </div>

                {/* Temporary Data Migration Tool */}
                <div className="mt-8 p-4 border-2 border-dashed border-warning rounded-lg">
                    <h4 className="text-warning font-bold mb-2">Temporary Data Migration Tool</h4>
                    <p className="text-sm text-muted mb-4">Use this tool to update existing series with the new date and field structures. Select a series, set the start date for Week 1, and run the migration.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label htmlFor="migrationStartDate" className="block text-xs font-medium text-muted">Week 1 Start Date</label>
                            <input type="date" id="migrationStartDate" value={migrationStartDate} onChange={e => setMigrationStartDate(e.target.value)} className="input-field w-full" />
                        </div>
                        <div>
                            <label htmlFor="migrationDayOfWeek" className="block text-xs font-medium text-muted">Recurring Day</label>
                            <select id="migrationDayOfWeek" value={migrationDayOfWeek} onChange={e => setMigrationDayOfWeek(e.target.value)} className="select-field w-full">
                                <option value="1">Monday</option>
                                <option value="2">Tuesday</option>
                                <option value="3">Wednesday</option>
                                <option value="4">Thursday</option>
                                <option value="5">Friday</option>
                                <option value="6">Saturday</option>
                                <option value="0">Sunday</option>
                            </select>
                        </div>
                        <button onClick={handleRunMigration} disabled={!selectedSeason || !migrationStartDate} className="btn btn-warning w-full">
                            Run Migration on Selected Series
                        </button>
                    </div>
                </div>

                {/* Temporary Raw Data Display */}
                <div className="mt-8 p-4 border-2 border-dashed border-danger rounded-lg">
                    <h4 className="text-danger font-bold mb-2">Temporary Raw Data View</h4>
                    <pre className="bg-neutral-soft p-3 rounded text-xs overflow-auto max-h-96">{JSON.stringify(rawLoadedData, null, 2)}</pre>
                </div>

            </div>

            {/* Player Entries Modal */}
            <PlayerEntriesModal
                isOpen={isEntriesModalOpen}
                onClose={() => { setIsEntriesModalOpen(false); setSelectedPlayer(null); }}
                player={selectedPlayer}
                mysteryOutEntries={mysteryOutEntries}
                weekInfoEntries={weekInfoEntries} /* Pass weekInfoEntries */
                overallHighestOut={overallHighestOut}
                onEditEntryClick={(playerId, entryWeek) => {
                    handleEditPlayerEntry(playerId, entryWeek);
                    setIsEntriesModalOpen(false); // Close modal after initiating edit
                }}
            />

            {/* Overwrite Confirmation Modal */}
            <OverwriteConfirmationModal
                isOpen={isOverwriteModalOpen}
                onClose={handleDiscardEntry}
                onConfirm={handleConfirmOverwrite}
                onDiscard={handleDiscardEntry}
                entryData={overwritePendingEntry}
            />

            {/* Create New Series Modal */}
            {isCreateSeriesModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4 z-50 bg-black/60 backdrop-blur-sm">
                    <div className="card rounded-xl shadow-2xl p-6 w-full max-w-md relative border border-primary-two">
                        <h3 className="text-xl font-semibold text-strong mb-4">Create New Series</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="newSeriesName" className="block text-sm font-medium text-muted mb-1">Series Name</label>
                                <input id="newSeriesName" type="text" value={newSeriesName} onChange={e => setNewSeriesName(e.target.value)} className="input-field w-full" placeholder="e.g., Summer Point Series" />
                            </div>
                            <div>
                                <label htmlFor="newSeriesStartDate" className="block text-sm font-medium text-muted mb-1">Start Date</label>
                                <input id="newSeriesStartDate" type="date" value={newSeriesStartDate} onChange={e => setNewSeriesStartDate(e.target.value)} className="input-field w-full" />
                            </div>
                            <div>
                                <label htmlFor="newSeriesDayOfWeek" className="block text-sm font-medium text-muted mb-1">Recurring Day</label>
                                <select id="newSeriesDayOfWeek" value={newSeriesDayOfWeek} onChange={e => setNewSeriesDayOfWeek(e.target.value)} className="select-field w-full">
                                    <option value="1">Monday</option>
                                    <option value="2">Tuesday</option>
                                    <option value="3">Wednesday</option>
                                    <option value="4">Thursday</option>
                                    <option value="5">Friday</option>
                                    <option value="6">Saturday</option>
                                    <option value="0">Sunday</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="newSeriesWeeks" className="block text-sm font-medium text-muted mb-1">Number of Weeks</label>
                                <input id="newSeriesWeeks" type="number" value={newSeriesWeeks} onChange={e => setNewSeriesWeeks(e.target.value)} className="input-field w-full" min="1" />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-4 mt-6">
                            <button onClick={() => setIsCreateSeriesModalOpen(false)} className="btn btn-secondary">Cancel</button>
                            <button onClick={handleCreateSeries} className="btn btn-success">Create</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Series Name Modal */}
            {isEditNameModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4 z-50 bg-black/60 backdrop-blur-sm">
                    <div className="card rounded-xl shadow-2xl p-6 w-full max-w-md relative border border-primary-two">
                        <h3 className="text-xl font-semibold text-strong mb-4">Edit Series Name</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="editedSeriesName" className="block text-sm font-medium text-muted mb-1">Series Name</label>
                                <input id="editedSeriesName" type="text" value={editedSeriesName} onChange={e => setEditedSeriesName(e.target.value)} className="input-field w-full" />
                            </div>
                            <div>
                                <label htmlFor="editedSeriesYear" className="block text-sm font-medium text-muted mb-1">Year</label>
                                <input id="editedSeriesYear" type="text" value={editedSeriesYear} onChange={e => setEditedSeriesYear(e.target.value)} className="input-field w-full" />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-4 mt-6">
                            <button onClick={() => setIsEditNameModalOpen(false)} className="btn btn-secondary">Cancel</button>
                            <button onClick={handleSaveSeriesMeta} className="btn btn-primary">Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Status Modal */}
            {isUpdateStatusModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4 z-50 bg-black/60 backdrop-blur-sm">
                    <div className="card rounded-xl shadow-2xl p-6 w-full max-w-md relative border border-primary-two">
                        <h3 className="text-xl font-semibold text-strong mb-4">Update Series Status</h3>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-muted">Active:</label>
                                <div className="flex items-center gap-2">
                                    <input type="radio" id="statusActive" name="activeStatus" checked={tempIsActive} onChange={() => setTempIsActive(true)} />
                                    <label htmlFor="statusActive">Yes</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="radio" id="statusInactive" name="activeStatus" checked={!tempIsActive} onChange={() => setTempIsActive(false)} />
                                    <label htmlFor="statusInactive">No</label>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-muted">Archived:</label>
                                <div className="flex items-center gap-2">
                                    <input type="radio" id="statusArchived" name="archivedStatus" checked={tempIsArchived} onChange={() => setTempIsArchived(true)} />
                                    <label htmlFor="statusArchived">Yes</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="radio" id="statusNotArchived" name="archivedStatus" checked={!tempIsArchived} onChange={() => setTempIsArchived(false)} />
                                    <label htmlFor="statusNotArchived">No</label>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-4 mt-6">
                            <button onClick={() => setIsUpdateStatusModalOpen(false)} className="btn btn-secondary">Cancel</button>
                            <button onClick={handleUpdateStatus} className="btn btn-primary">Update</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PointSeriesAdminPage;